import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { updateClient } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { useWorkflowOptional } from "../../workflow/WorkflowContext";

// A2-491 (operator 2026-10-22) — UK_ONLINE_EVENT_TAX_CHOICE_REQUIRED: a UK
// client's live online event sold to an EU consumer is claimed by UK law and
// by EU law, and the client picks which country taxes those sales. The choice
// is a client setting (uk_online_event_eu_consumer_vat), the same field the
// client form edits; saved here, applied by re-running the calculation.
export const CHOICES = ["UK", "CUSTOMER_COUNTRY"];

export const OnlineEventVatChoiceCta = ({ g, clientId, calcFrozen = false }) => {
  const { admin } = useAuth();
  const { t } = useTranslation();
  const wf = useWorkflowOptional();
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [recalcFailed, setRecalcFailed] = useState(null);
  // A2-582: drawn where the server offers this answer (GroupAnswers).
  if (!clientId) return null;
  const k = (s) => t(`ui.admin_clients.form.online_event_vat${s}`);
  const editUrl = `/admin/clients?edit=${clientId}&section=oss`;
  // Only an admin can change a client (PATCH /api/clients is ADMIN-only).
  if (admin?.role !== "ADMIN") {
    return <p className="mt-2 text-xs text-slate-500" data-testid="online-event-vat-hint">{k("_hint")}</p>;
  }
  const save = async () => {
    setBusy(true); setError(null); setRecalcFailed(null);
    try {
      await updateClient(clientId, { uk_online_event_eu_consumer_vat: choice });
      setSaved(true);
      // The choice is a client setting: it applies when the calc re-runs.
      // runCalc never throws; a falsy result is a failed recalculation (the
      // choice IS saved either way), as in PropertyLettingResolver.
      if (wf?.runCalc && !(await wf.runCalc())) {
        setRecalcFailed(t("ui.review_grouped.est_toast_recalc_fail", {
          defaultValue: "Saved, but the recalculation failed — the rows may not reflect it yet.",
        }));
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  // A frozen (filed) calc is immutable: the choice is changed by a new calc
  // that supersedes it — the standard frozen message, controls disabled.
  const frozenMsg = calcFrozen ? t("ui.review_grouped.frozen_banner") : null;
  return (
    <div className="mt-2 space-y-1.5" data-testid="online-event-vat-cta">
      <label className="text-xs text-slate-600 dark:text-slate-300" htmlFor="online-event-vat-select">{k("")}</label>
      <div className="flex flex-wrap items-center gap-2">
        <select id="online-event-vat-select" data-testid="online-event-vat-select" value={choice}
                disabled={calcFrozen} title={frozenMsg || undefined}
                onChange={(e) => { setChoice(e.target.value); setSaved(false); }}
                className="h-9 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-sm px-3 rounded-md">
          <option value="">{k("_unset")}</option>
          {CHOICES.map((c) => <option key={c} value={c}>{k(`_${c}`)}</option>)}
        </select>
        <button type="button" data-testid="online-event-vat-save" disabled={calcFrozen || !choice || busy} onClick={save} title={frozenMsg || undefined}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold text-xs px-3 py-2 rounded-full">
          {k("_save")}
        </button>
        <Link to={editUrl} className="text-xs underline" data-testid="online-event-vat-open">{k("_open")}</Link>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">{k("_hint")}</p>
      {frozenMsg && <p className="text-xs text-slate-500" data-testid="online-event-vat-frozen">{frozenMsg}</p>}
      {recalcFailed && <p className="text-xs text-amber-700 dark:text-amber-300" role="alert" data-testid="online-event-vat-recalc-failed">{recalcFailed}</p>}
      {saved && <p className="text-xs text-emerald-700 dark:text-emerald-300" data-testid="online-event-vat-saved">{k("_saved")}</p>}
      {error && <p className="text-xs text-rose-600" role="alert">{error}</p>}
    </div>
  );
};
